import { createServerFn } from "@tanstack/react-start";

export interface IndexStatusRow {
  url: string;
  coverage_state: string | null;
  verdict: string | null;
  indexing_state: string | null;
  last_crawl_time: string | null;
  first_indexed_at: string | null;
  last_checked_at: string;
  updated_at: string;
}

export interface IndexStatusSummary {
  total: number;
  indexed: number;
  crawledNotIndexed: number;
  discoveredNotIndexed: number;
  excluded: number;
  error: number;
  other: number;
  lastCheckedAt: string | null;
  recentlyDiscovered: IndexStatusRow[];
  recent: IndexStatusRow[];
  byState: { state: string; count: number }[];
}

function bucket(coverage: string | null | undefined) {
  const c = (coverage ?? "").toLowerCase();
  if (c.includes("submitted and indexed") || c.includes("indexed, not submitted")) return "indexed";
  if (c.includes("crawled") && c.includes("not indexed")) return "crawledNotIndexed";
  if (c.includes("discovered")) return "discoveredNotIndexed";
  if (c.includes("excluded") || c.includes("duplicate") || c.includes("alternate")) return "excluded";
  if (c.includes("error")) return "error";
  return "other";
}

export const getIndexingOverview = createServerFn({ method: "GET" }).handler(
  async (): Promise<IndexStatusSummary> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("url_index_status")
      .select(
        "url, coverage_state, verdict, indexing_state, last_crawl_time, first_indexed_at, last_checked_at, updated_at",
      )
      .order("updated_at", { ascending: false })
      .limit(2000);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as IndexStatusRow[];

    const summary: IndexStatusSummary = {
      total: rows.length,
      indexed: 0,
      crawledNotIndexed: 0,
      discoveredNotIndexed: 0,
      excluded: 0,
      error: 0,
      other: 0,
      lastCheckedAt: rows[0]?.last_checked_at ?? null,
      recentlyDiscovered: [],
      recent: rows.slice(0, 25),
      byState: [],
    };

    const stateMap = new Map<string, number>();
    for (const r of rows) {
      const key = bucket(r.coverage_state);
      (summary as any)[key] += 1;
      const label = r.coverage_state ?? "Unknown";
      stateMap.set(label, (stateMap.get(label) ?? 0) + 1);
    }
    summary.byState = [...stateMap.entries()]
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count);

    summary.recentlyDiscovered = rows
      .filter((r) => r.first_indexed_at)
      .sort((a, b) => (b.first_indexed_at ?? "").localeCompare(a.first_indexed_at ?? ""))
      .slice(0, 20);

    return summary;
  },
);